<template>
  <Section
    :id="id"
    :header="t('tenderInfo.tenderInfo')"
    :loading="loading"
    class="shipment-tender-info-section"
    data-class="shipment-tender-info-section-data"
  >
    <template #headerRight>
      <!-- TODO: Move v-if back to template: https://rygen.atlassian.net/browse/CS-7718 -->
      <ExpandButton
        v-if="showTerminalInfoButton"
        v-model="showTerminalInfo"
        target-id="terminal-information"
        :false-label="
          t('common.showParam', {
            param: t('common.paramInformation', {
              param: t('terminals.terminals'),
            }),
          })
        "
        :true-label="
          t('common.hideParam', {
            param: t('common.paramInformation', {
              param: t('terminals.terminals'),
            }),
          })
        "
      />
    </template>

    <template v-if="!loading">
      <div class="row">
        <div class="shipment-tender-info-cell">
          <KeyValuePair :label="t('tenderInfo.status')" uppercase-label>
            <template #value>
              <ShipmentTenderStatusChip
                :request-status="tenderRequest.status"
                :response-code="tenderRequest.tenderResponse?.responseCode"
              />
            </template>
          </KeyValuePair>
        </div>

        <div class="shipment-tender-info-cell">
          <KeyValuePair
            :label="t('tenderInfo.sentDate')"
            :value="`${tenderRequest.createdBy}\n${renderDateTime(tenderRequest.createdDate)}`"
            uppercase-label
          >
            <template #value>
              <div>{{ tenderRequest.createdBy }}</div>
              <div>{{ renderDateTime(tenderRequest.createdDate) }}</div>
            </template>
          </KeyValuePair>
        </div>
        <div class="shipment-tender-info-cell">
          <KeyValuePair
            :label="t('tenderInfo.expirationDate')"
            :value="renderDateTime(tenderRequest.expirationDate)"
            uppercase-label
          />
        </div>
        <div class="shipment-tender-info-cell">
          <KeyValuePair
            :label="t('tenderInfo.method')"
            :value="tenderRequest.tenderMethod"
            uppercase-label
          />
        </div>
      </div>
      <div v-if="displayResponseInfo" class="row">
        <div class="shipment-tender-info-cell">
          <KeyValuePair
            :label="t('tenderInfo.responseCode')"
            :value="responseCodeTooltip"
            uppercase-label
          >
            <template #value>
              <div>
                {{
                  tenderRequest.tenderResponse?.responseCode
                    ? translateEnum(
                        tenderRequest.tenderResponse.responseCode,
                        'TenderResponseCode',
                      )
                    : ''
                }}
              </div>
              <div>{{ tenderRequest.tenderResponse?.responseReason }}</div>
            </template>
          </KeyValuePair>
        </div>
        <div class="shipment-tender-info-cell">
          <KeyValuePair
            :label="t('tenderInfo.responseDate')"
            :value="renderDateTime(tenderRequest.tenderResponse?.responseDate)"
            uppercase-label
          />
        </div>
        <div class="shipment-tender-info-cell">
          <KeyValuePair
            :label="t('tenderInfo.responsePickupDate')"
            :value="
              renderDateTime(tenderRequest.tenderResponse?.responsePickupDate)
            "
            uppercase-label
          />
        </div>
        <div class="shipment-tender-info-cell">
          <KeyValuePair
            :label="t('tenderInfo.responseMethod')"
            :value="tenderRequest.tenderResponse?.responseMethod"
            uppercase-label
          />
        </div>
      </div>
      <div v-if="displayResponseInfo" class="row">
        <div class="shipment-tender-info-cell">
          <KeyValuePair
            :label="t('tenderInfo.responseComment')"
            :value="tenderRequest.tenderResponse?.responseComment"
            uppercase-label
          />
        </div>
      </div>
      <div
        v-if="tenderRequest.tenderResponse?.responseReferenceNumbers.length"
        class="row flex-gap-1 flex-wrap"
      >
        <div
          v-for="(v, i) of tenderRequest.tenderResponse
            ?.responseReferenceNumbers"
          :key="i"
          class="shipment-tender-info-cell"
        >
          <KeyValuePair
            :label="v.typeNaturalId"
            :value="v.value"
            uppercase-label
          />
        </div>
      </div>
      <div v-show="showTerminalInfo" id="terminal-information">
        <i18n-t
          v-if="
            terminalInfo.error === INVALID_CREDENTIALS &&
            canUpdateCommercialIntegration
          "
          keypath="terminals.errors.invalidCredentialsDetails"
          tag="div"
          class="row"
        >
          <template #link>
            <a :href="commercialIntegrationUrl" class="configuration-link">
              {{ t('terminals.errors.invalidCredentialsDetailsLinkText') }}
            </a>
          </template>
        </i18n-t>
        <div v-else-if="terminalInfo.error" class="row">
          <p>
            {{
              terminalInfo.error === INVALID_CREDENTIALS
                ? t('terminals.errors.invalidCredentials')
                : terminalInfoErrorMessage
            }}
          </p>
        </div>
        <div v-else class="row">
          <div class="shipment-tender-info-cell">
            <KeyValuePair
              :label="`${t('orders.origin')} - ${t('contacts.contacts')}`"
              :value="originContactForTooltip"
              uppercase-label
            >
              <template v-if="!terminalInfo.loading" #value>
                <div>
                  {{ terminalInfo.data?.originContact?.contactName ?? '-' }}
                </div>
                <div>
                  {{ terminalInfo.data?.originContact?.contactEmail ?? '-' }}
                </div>
                <div>
                  {{
                    terminalInfo.data?.originContact?.contactPhoneNumber ?? '-'
                  }}
                </div>
              </template>
              <template v-else #value>
                <Skeleton width="100%" class="mb-2 bg-gray-dark" />
                <Skeleton width="100%" class="mb-1 bg-gray" />
                <Skeleton width="75%" class="mb-1 bg-gray" />
              </template>
            </KeyValuePair>
          </div>

          <div class="shipment-tender-info-cell">
            <KeyValuePair
              :label="`${t('orders.origin')} - ${t('locations.locations')}`"
              :value="originAddressForTooltip"
              uppercase-label
            >
              <template v-if="!terminalInfo.loading" #value>
                <NamedAddress
                  :name="terminalInfo.data?.originLocation?.locationName"
                  :address="originAddress"
                />
              </template>
              <template v-else #value>
                <Skeleton width="100%" class="mb-2 bg-gray-dark" />
                <Skeleton width="100%" height="2.5rem" class="bg-gray" />
              </template>
            </KeyValuePair>
          </div>

          <div class="shipment-tender-info-cell">
            <KeyValuePair
              :label="`${t('orders.destination')} - ${t('contacts.contacts')}`"
              :value="destinationContactForTooltip"
              uppercase-label
            >
              <template v-if="!terminalInfo.loading" #value>
                <div>
                  {{
                    terminalInfo.data?.destinationContact?.contactName ?? '-'
                  }}
                </div>
                <div>
                  {{
                    terminalInfo.data?.destinationContact?.contactEmail ?? '-'
                  }}
                </div>
                <div>
                  {{
                    terminalInfo.data?.destinationContact?.contactPhoneNumber ??
                    '-'
                  }}
                </div>
              </template>
              <template v-else #value>
                <Skeleton width="100%" class="mb-2 bg-gray-dark" />
                <Skeleton width="100%" class="mb-1 bg-gray" />
                <Skeleton width="75%" class="mb-1 bg-gray" />
              </template>
            </KeyValuePair>
          </div>

          <div class="shipment-tender-info-cell">
            <KeyValuePair
              :label="`${t('orders.destination')} - ${t(
                'locations.locations',
              )}`"
              :value="destinationAddressForTooltip"
              uppercase-label
            >
              <template v-if="!terminalInfo.loading" #value>
                <NamedAddress
                  :name="terminalInfo.data?.destinationLocation?.locationName"
                  :address="destinationAddress"
                />
              </template>
              <template v-else #value>
                <Skeleton width="100%" class="mb-2 bg-gray-dark" />
                <Skeleton width="100%" height="2.5rem" class="bg-gray" />
              </template>
            </KeyValuePair>
          </div>
        </div>
        <Message
          v-if="terminalInfo.data?.message"
          class="shipment-tender-info-cell text-danger rygen-message"
          severity="error"
        >
          {{ terminalInfo.data.message }}
        </Message>
      </div>
    </template>
    <template v-else>
      <div class="row">
        <div class="shipment-tender-info-cell">
          <Skeleton width="100%" class="mb-2"></Skeleton>
          <Skeleton width="75%"></Skeleton>
        </div>
        <div class="shipment-tender-info-cell">
          <Skeleton width="100%" class="mb-2"></Skeleton>
          <Skeleton width="75%"></Skeleton>
        </div>
      </div>
    </template>
  </Section>
</template>

<script lang="ts" setup>
import axios from 'axios';
import type { Ref } from 'vue';
import { computed, ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import authorization from '@rygen/rygen-ui/authorization';
import { useToast } from '@rygen/rygen-ui/composables';
import environmentPropertiesStore from '@rygen/rygen-ui/env/EnvironmentPropertiesStore';
import { renderDateTime, translateEnum } from '@rygen/rygen-ui/i18n';
import {
  Address,
  CapacityProviderType,
  Loadable,
  Mode,
} from '@rygen/rygen-ui/model';

import api from '@/api';
import { renderFullAddress } from '@/composables';
import { TenderRequest, TenderStatus, TerminalInfo } from '@/model';
import { useCurrentUserStore } from '@/stores/CurrentUser';
import { getErrorResponse } from '@/util';

import ShipmentTenderStatusChip from '@/components/shipment/ShipmentTenderStatusChip.vue';
import NamedAddress from '@rygen/rygen-ui/components/address/NamedAddress.vue';
import ExpandButton from '@rygen/rygen-ui/components/button/ExpandButton.vue';
import KeyValuePair from '@rygen/rygen-ui/components/ui/KeyValuePair.vue';
import Section from '@rygen/rygen-ui/components/ui/Section.vue';
import Message from 'primevue/message';
import Skeleton from 'primevue/skeleton';

const INVALID_CREDENTIALS = 'Invalid CarrierConnect credentials';

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  loading: {
    type: Boolean,
    default: () => false,
  },
  tenderRequest: {
    type: TenderRequest,
    required: true,
  },
});
const { id, loading, tenderRequest } = toRefs(props);
const { t } = useI18n();
const toast = useToast();
const { selectedDomain, selectedDomainId } = useCurrentUserStore();

const showTerminalInfo = ref(false);
const carrierConnectEnabled = ref(false);
const terminalInfo: Ref<Loadable<TerminalInfo>> = ref({ loading: false });

const hasTerminalPermission = computed(() =>
  authorization.hasAuthority('commercialIntegration', 'read'),
);

const canUpdateCommercialIntegration = computed(
  () =>
    authorization.hasAuthority('commercialIntegration', 'delete') &&
    authorization.hasAuthority('commercialIntegration', 'create'),
);

const displayResponseInfo = computed(() => {
  return tenderRequest.value.status === TenderStatus.RESPONSE_RECEIVED;
});

const responseCodeString = computed(() => {
  return tenderRequest.value.tenderResponse?.responseCode
    ? translateEnum(
        tenderRequest.value.tenderResponse.responseCode,
        'TenderResponseCode',
      )
    : '';
});

const responseReasonString = computed(
  () => tenderRequest.value.tenderResponse?.responseReason,
);

const responseCodeTooltip = computed(() => {
  return [responseCodeString.value, responseReasonString.value].join('\n');
});

const showTerminalInfoButton = computed(() => {
  const hasCarrierScac =
    tenderRequest.value.carrier?.type === CapacityProviderType.CARRIER &&
    !!tenderRequest.value.carrier?.scac;
  const hasBrokeredCarrierScac =
    tenderRequest.value.carrier?.type === CapacityProviderType.BROKER &&
    !!tenderRequest.value.brokeredCarrier?.scac;
  const hasValidScac = hasCarrierScac || hasBrokeredCarrierScac;

  return (
    tenderRequest.value.mode === Mode.LTL &&
    hasValidScac &&
    hasTerminalPermission.value &&
    carrierConnectEnabled.value
  );
});

const originContactForTooltip = computed(() => {
  if (terminalInfo.value.loading) {
    return;
  }

  const contact = terminalInfo.value.data?.originContact;
  const name = contact?.contactName ?? '-';
  const email = contact?.contactEmail ?? '-';
  const phone = contact?.contactPhoneNumber ?? '-';

  return `${name}\n${email}\n${phone}`;
});

const originAddress = computed(
  () => terminalInfo?.value.data?.originLocation?.toAddress() ?? new Address(),
);

const originAddressForTooltip = computed(() => {
  if (terminalInfo.value.loading) {
    return;
  }

  const name = terminalInfo.value.data?.originLocation?.locationName ?? '';
  const address = renderFullAddress(originAddress.value);
  return `${name}\n${address}`;
});

const destinationContactForTooltip = computed(() => {
  if (terminalInfo.value.loading) {
    return;
  }

  const contact = terminalInfo.value.data?.destinationContact;
  const name = contact?.contactName ?? '-';
  const email = contact?.contactEmail ?? '-';
  const phone = contact?.contactPhoneNumber ?? '-';

  return `${name}\n${email}\n${phone}`;
});

const destinationAddress = computed(
  () =>
    terminalInfo?.value.data?.destinationLocation?.toAddress() ?? new Address(),
);

const destinationAddressForTooltip = computed(() => {
  if (terminalInfo.value.loading) {
    return;
  }

  const name = terminalInfo.value.data?.originLocation?.locationName ?? '';
  const address = renderFullAddress(destinationAddress.value);
  return `${name}\n${address}`;
});

const terminalInfoErrorMessage = computed(() => {
  return t('errors.loadingParam', {
    param: t('common.paramInformation', { param: t('terminals.terminals') }),
  });
});

const commercialIntegrationUrl = computed(() => {
  const ficonUrl = environmentPropertiesStore.getValue('FICON_URL');
  const domainParam = selectedDomain.value?.naturalId
    ? `?domain-context=${encodeURIComponent(selectedDomain.value.naturalId)}`
    : '';
  return `${ficonUrl}commercial-integration${domainParam}`;
});

const getTerminalInformation = async () => {
  if (!hasTerminalPermission.value) {
    return;
  }

  try {
    if (!tenderRequest.value.shipmentId) {
      throw new Error('No shipment id');
    }

    terminalInfo.value.loading = true;
    terminalInfo.value.data = await api.shipment.getTerminalInformation(
      tenderRequest.value.shipmentId,
    );
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('Error getting Terminal Information', e);
    if (axios.isAxiosError(e)) {
      const errorResponse = getErrorResponse(e);
      if (errorResponse.exceptionMessage === INVALID_CREDENTIALS) {
        terminalInfo.value.error = INVALID_CREDENTIALS;
        toast.addError({
          detail: t('terminals.errors.invalidCredentials'),
          error: e,
        });
        return;
      }
    }

    terminalInfo.value.error = e;
    toast.addError({
      detail: terminalInfoErrorMessage.value,
      error: e,
    });
  } finally {
    terminalInfo.value.loading = false;
  }
};

const getCarrierConnectInfo = async () => {
  try {
    carrierConnectEnabled.value = await api.client.isCarrierConnectEnabled();
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('Error getting Carrier Connect Information', e);
  }
};

watch(showTerminalInfo, (newVal: boolean) => {
  if (newVal && !terminalInfo.value.data && !terminalInfo.value.error) {
    getTerminalInformation();
  }
});

watch(
  () => tenderRequest.value.shipmentId,
  () => {
    terminalInfo.value.data = undefined;
    showTerminalInfo.value = false;
  },
);

watch(
  selectedDomainId,
  () => {
    getCarrierConnectInfo();
  },
  { immediate: true },
);
</script>

<style>
.shipment-tender-info-section {
  .shipment-tender-info-section-data {
    .row {
      display: flex;

      .shipment-tender-info-cell {
        padding: var(--sp-3);
        flex: 1 0 auto;
        align-items: center;
        width: 20%;

        .key-value-pair-value {
          white-space: pre-wrap;
        }
      }
    }

    #terminal-information {
      .p-message {
        margin-bottom: var(--sp-3);
      }

      .configuration-link {
        &::before {
          content: '';
          margin-left: var(--sp-1);
        }
      }
    }
  }
}
</style>
